import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { getFinancialAssetRequestFromNotCompletedLoanForm } from '@app/components/not-completed-loan-form/not-completed-loan-form.helper';
import { SectionAFormGroup } from '@app/components/project-wizard/+helpers/section-a-form.helper';
import { SectionGFormGroup } from '@app/components/project-wizard/+helpers/section-g-form.helper';
import { SectionJFormGroup } from '@app/components/project-wizard/+helpers/section-j-form.helper';
import { StepType } from '@app/components/wizard/wizard.component';
import {
  fileFormArrayToFormDataArray,
  isBooleanLikeTrue,
  isFormValid,
} from '@app/helpers/form.helper';
import { FinancialAssetResponse } from '@app/models/financial-assets.models';
import { WizardSection } from '@app/models/project-setup.models';
import { ToastService } from '@app/services/toast.service';
import { StepSectionType, StepsStore } from '@app/stores/project-setup/steps';
import { wizardStepMap } from '@app/stores/project-setup/steps/steps.models';
import {
  SectionFormTypes,
  WizardStore,
} from '@app/stores/project-setup/wizard.store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { FileData, UploadService } from '../file/upload.service';
import { FinancialAssetService } from '../financial-asset/financial-asset.service';
import { ProjectSetupService } from '../project-setup/project-setup.service';

const NOT_PERFORMING_LOAN_CONNECTION_TYPE = 3;
const FOUNDING_MODE_LOAN_PURPOSE = 2;
const DISABLED_STEP_INDICES_WHEN_LOAN_PURPOSE: number[] = [4, 5];

export interface ProjectWizardInitFromBaseData {
  projectName: string;
  contactUserId: string | null;
  state: number;
}

@Injectable({ providedIn: 'root' })
export class StepManagerService {
  private financialAssetsService = inject(FinancialAssetService);
  private uploadService = inject(UploadService);
  private stepsStore = inject(StepsStore);
  private wizardStore = inject(WizardStore);
  private projectSetupService = inject(ProjectSetupService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  private acutalStep: StepSectionType = StepSectionType.baseData;

  constructor() {}

  public setActiveStep(step: StepSectionType) {}

  public initializeStepsFromData(
    projectId: string,
  ): Observable<ProjectWizardInitFromBaseData> {
    const steps = this.stepsStore.orderedSections();
    const currentStepsMap = this.stepsStore.stepsBySectionTpye();
    const sectionObservables = steps.map((stepType, index) => {
      const section = wizardStepMap.get(stepType);
      if (!section) {
        return of(null);
      }
      return this.wizardStore.getSection(projectId, section).pipe(
        map(() => ({ index, section })),
        catchError(() => of(null)),
      );
    });

    return forkJoin([
      this.projectSetupService.getProjectBaseData(projectId),
      ...sectionObservables,
    ]).pipe(
      tap(([baseData, ...results]) => {
        const base = baseData as { foundingMode?: number } | null;
        const isLoanPurpose =
          base?.foundingMode === FOUNDING_MODE_LOAN_PURPOSE;

        const updates: { index: number; status: StepType }[] = [];

        (
          results as Array<{ index: number; section: WizardSection } | null>
        ).forEach((result) => {
          if (!result) return;
          const { index, section } = result;
          if (
            isLoanPurpose &&
            DISABLED_STEP_INDICES_WHEN_LOAN_PURPOSE.includes(index)
          ) {
            return;
          }
          const stepTypeEnum = steps[index];
          const currentStep = currentStepsMap.get(stepTypeEnum);

          if (currentStep?.stepType === StepType.locked) {
            return;
          }

          const form = this.wizardStore.getSectionForm(
            section as Exclude<WizardSection, WizardSection.Base>,
          );
          if (form) {
            if (isFormValid(form)) {
              updates.push({ index, status: StepType.done });
            } else {
              updates.push({ index, status: StepType.hasWarrning });
            }
          }
        });

        if (isLoanPurpose) {
          DISABLED_STEP_INDICES_WHEN_LOAN_PURPOSE.forEach((index) => {
            updates.push({ index, status: StepType.disabled });
          });
        }

        const doneIndices = new Set(
          updates.filter((u) => u.status === StepType.done).map((u) => u.index),
        );
        doneIndices.forEach((index) => {
          const nextIndex = index + 1;
          if (nextIndex < steps.length && !doneIndices.has(nextIndex)) {
            const nextStepUpdated = updates.some((u) => u.index === nextIndex);
            if (!nextStepUpdated) {
              const nextStepTypeEnum = steps[nextIndex];
              const nextStep = currentStepsMap.get(nextStepTypeEnum);
              if (nextStep?.stepType === StepType.locked) {
                updates.push({ index: nextIndex, status: StepType.available });
              }
            }
          }
        });

        if (updates.length > 0) {
          this.stepsStore.batchUpdateStepStatus(updates);
        }
      }),
      map(([baseData]) => {
        const base = baseData as {
          name?: string;
          contactUserId?: string | null;
          state: number;
        } | null;
        const projectName = base?.name?.trim() ?? '';
        const contactUserId =
          base?.contactUserId != null && String(base.contactUserId).trim() !== ''
            ? String(base.contactUserId).trim()
            : null;
        const state = base!.state;
        return { projectName, contactUserId, state };
      }),
    );
  }

  public onStepClick(stepIndex: number) {
    this.stepsStore.setStepToActive(stepIndex);
    this.acutalStep = stepIndex;
  }

  public onNextClick(projectId: string) {
    const currentIndex = this.stepsStore.stepIndex();
    const wizardStep = this.stepsStore.actualSteps()[currentIndex];
    if (wizardStep.stepType === StepType.disabled) {
      const nextIndex = this.getNextNonDisabledStepIndex(currentIndex);
      if (nextIndex > currentIndex) {
        const nextStep = this.stepsStore.actualSteps()[nextIndex];
        if (nextStep?.stepType !== StepType.done) {
          this.stepsStore.markStepAsAvailable(nextIndex);
        }
        this.stepsStore.updateStepIndex(nextIndex);
      }
      return;
    }
    const section = wizardStepMap.get(wizardStep.sectionType);
    if (section === undefined) {
      if (wizardStep.stepType !== StepType.locked) {
        this.stepsStore.markStepAsDone(currentIndex);
      }
      const nextIndex = this.getNextNonDisabledStepIndex(currentIndex);
      if (nextIndex > currentIndex) {
        const nextStep = this.stepsStore.actualSteps()[nextIndex];
        if (nextStep?.stepType !== StepType.done) {
          this.stepsStore.markStepAsAvailable(nextIndex);
        }
      }
      this.stepsStore.updateStepIndex(nextIndex);
      return;
    }
    const wizardForm = this.wizardStore.getSectionForm(section);
    if (wizardForm === null || !isFormValid(wizardForm)) {
      this.stepsStore.markStepAsWarning(currentIndex);
      return;
    }
    this.saveData(wizardForm, section, projectId);
  }

  public onNextReadOnlyClick(): void {
    const currentIndex = this.stepsStore.stepIndex();
    const nextIndex = this.getNextNonDisabledStepIndex(currentIndex);
    if (nextIndex > currentIndex) {
      this.stepsStore.updateStepIndex(nextIndex);
    }
  }

  private getNextNonDisabledStepIndex(fromIndex: number): number {
    const steps = this.stepsStore.actualSteps();
    let next = fromIndex + 1;
    while (next < steps.length && steps[next]?.stepType === StepType.disabled) {
      next++;
    }
    return next < steps.length ? next : fromIndex;
  }

  private getPreviousNonDisabledStepIndex(fromIndex: number): number {
    const steps = this.stepsStore.actualSteps();
    let prev = fromIndex - 1;
    while (prev >= 0 && steps[prev]?.stepType === StepType.disabled) {
      prev--;
    }
    return prev >= 0 ? prev : fromIndex;
  }

  public onPreviousClick() {
    const currentIndex = this.stepsStore.stepIndex();
    const prevIndex = this.getPreviousNonDisabledStepIndex(currentIndex);
    if (prevIndex < currentIndex) {
      this.stepsStore.updateStepIndex(prevIndex);
    }
  }

  private getFinancialReports(form: FormGroup<SectionAFormGroup>): FileData[] {
    const lastYearReports = fileFormArrayToFormDataArray(
      form.controls.lastFinancialReports,
    );
    const secondYearReports = fileFormArrayToFormDataArray(
      form.controls.secondYearFinancialReports,
    );
    const thirdYearReports = fileFormArrayToFormDataArray(
      form.controls.thirdYearFinancialReports,
    );

    return [...lastYearReports, ...secondYearReports, ...thirdYearReports];
  }

  private getSectionJFiles(form: FormGroup<SectionJFormGroup>): FileData[] {
    const companyExtracts = fileFormArrayToFormDataArray(
      form.controls.companyExtracts,
    );
    const technicalUpload = fileFormArrayToFormDataArray(
      form.controls.technicalUpload,
    );
    const valuation = fileFormArrayToFormDataArray(form.controls.valuation);
    const mainImage = fileFormArrayToFormDataArray(form.controls.mainImage);
    const otherDocs = fileFormArrayToFormDataArray(form.controls.otherDocs);
    const images = fileFormArrayToFormDataArray(form.controls.images);
    const videos = fileFormArrayToFormDataArray(form.controls.videos);

    return [
      ...companyExtracts,
      ...technicalUpload,
      ...valuation,
      ...mainImage,
      ...otherDocs,
      ...images,
      ...videos,
    ];
  }

  private applySuccessfulSectionSaveFromIndex(currentIndex: number): void {
    const currentStep = this.stepsStore.actualSteps()[currentIndex];
    if (currentStep?.stepType !== StepType.locked) {
      this.stepsStore.markStepAsDone(currentIndex);
    }
    const nextIndex = this.getNextNonDisabledStepIndex(currentIndex);
    if (nextIndex > currentIndex) {
      const nextStep = this.stepsStore.actualSteps()[nextIndex];
      if (nextStep?.stepType !== StepType.done) {
        this.stepsStore.markStepAsAvailable(nextIndex);
      }
    }
    this.stepsStore.updateStepIndex(nextIndex);
  }

  private showSaveSuccess(): void {
    this.toast.success(this.translate.instant('admin.toasts.savedSuccessfully'));
  }

  private showSaveFailed(): void {
    this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
  }

  private saveSectionJWorkflow(
    projectId: string,
    form: FormGroup<SectionJFormGroup>,
  ): Observable<unknown> {
    const sectionJFiles = this.getSectionJFiles(form);
    if (sectionJFiles.length === 0) {
      return this.wizardStore.saveSection(
        projectId,
        WizardSection.J,
        form as FormGroup<SectionFormTypes>,
      );
    }
    return this.uploadService.uploadFilesForProject(projectId, sectionJFiles).pipe(
      switchMap((response) => {
        if (response === undefined) {
          return EMPTY;
        }
        return this.wizardStore.saveSection(
          projectId,
          WizardSection.J,
          form as FormGroup<SectionFormTypes>,
        );
      }),
    );
  }

  public saveSectionJWithUploads(projectId: string): Observable<void> {
    const currentIndex = this.stepsStore.stepIndex();
    const form = this.wizardStore.getSectionForm(WizardSection.J);
    if (form === null || !isFormValid(form)) {
      this.stepsStore.markStepAsWarning(currentIndex);
      return EMPTY;
    }
    return this.saveSectionJWorkflow(
      projectId,
      form as FormGroup<SectionJFormGroup>,
    ).pipe(
      tap(() => this.applySuccessfulSectionSaveFromIndex(currentIndex)),
      map(() => undefined),
    );
  }

  public saveCurrentSectionIfNeeded(projectId: string): Observable<void> {
    const currentIndex = this.stepsStore.stepIndex();
    const wizardStep = this.stepsStore.actualSteps()[currentIndex];
    const section = wizardStepMap.get(wizardStep.sectionType);

    if (section === undefined) {
      return of(undefined);
    }

    const form = this.wizardStore.getSectionForm(section);
    if (form === null || !form.dirty) {
      return of(undefined);
    }

    if (!isFormValid(form)) {
      this.stepsStore.markStepAsWarning(currentIndex);
      return EMPTY;
    }

    switch (section) {
      case WizardSection.A: {
        const sectionAForm = form as FormGroup<SectionAFormGroup>;
        const financialReports = this.getFinancialReports(sectionAForm);
        const upload$ =
          financialReports.length > 0
            ? this.uploadService.uploadFilesForProject(projectId, financialReports)
            : of(undefined);
        return upload$.pipe(
          switchMap((response) => {
            if (response === undefined && financialReports.length > 0) {
              return EMPTY;
            }
            return this.wizardStore.saveSection(projectId, section, form);
          }),
          tap(() => this.showSaveSuccess()),
          catchError((err) => {
            this.showSaveFailed();
            return throwError(() => err);
          }),
          map(() => undefined),
        );
      }
      case WizardSection.G:
        return this.wizardStore.saveSection(projectId, section, form).pipe(
          switchMap(() =>
            this.syncSectionGFinancialAssets(
              projectId,
              form as FormGroup<SectionGFormGroup>,
            ),
          ),
          tap(() => this.showSaveSuccess()),
          catchError((err) => {
            this.showSaveFailed();
            return throwError(() => err);
          }),
          map(() => undefined),
        );
      case WizardSection.J:
        return this.saveSectionJWorkflow(
          projectId,
          form as FormGroup<SectionJFormGroup>,
        ).pipe(
          tap(() => this.showSaveSuccess()),
          catchError((err) => {
            this.showSaveFailed();
            return throwError(() => err);
          }),
          map(() => undefined),
        );
      default:
        return this.wizardStore
          .saveSection(projectId, section, form)
          .pipe(
            tap(() => this.showSaveSuccess()),
            catchError((err) => {
              this.showSaveFailed();
              return throwError(() => err);
            }),
          )
          .pipe(map(() => undefined));
    }
  }

  private saveData(
    form: FormGroup<SectionFormTypes>,
    section: Exclude<WizardSection, WizardSection.Base>,
    projectId: string,
  ): void {
    const currentIndex = this.stepsStore.stepIndex();
    const onSuccess = () => {
      this.showSaveSuccess();
      this.applySuccessfulSectionSaveFromIndex(currentIndex);
    };
    const onError = () => this.showSaveFailed();
    switch (section) {
      case WizardSection.A:
        if (form.dirty) {
          this.uploadService
            .uploadFilesForProject(
              projectId,
              this.getFinancialReports(form as FormGroup<SectionAFormGroup>),
            )
            .subscribe({
              next: (response) => {
                if (response !== undefined) {
                  this.wizardStore
                    .saveSection(projectId, section, form)
                    .subscribe({ next: onSuccess, error: onError });
                }
              },
              error: onError,
            });
        } else {
          onSuccess();
        }

        break;
      case WizardSection.D:
        onSuccess();
        break;
      case WizardSection.G:
        this.wizardStore
          .saveSection(projectId, section, form)
          .pipe(
            switchMap(() =>
              this.syncSectionGFinancialAssets(
                projectId,
                form as FormGroup<SectionGFormGroup>,
              ),
            ),
          )
          .subscribe({
            next: onSuccess,
            error: onError,
          });
        break;
      case WizardSection.J: {
        this.saveSectionJWorkflow(
          projectId,
          form as FormGroup<SectionJFormGroup>,
        ).subscribe({
          next: () => onSuccess(),
          error: onError,
        });
        break;
      }
      default:
        this.wizardStore
          .saveSection(projectId, section, form)
          .subscribe({ next: onSuccess, error: onError });
    }
  }

  private syncSectionGFinancialAssets(
    projectId: string,
    form: FormGroup<SectionGFormGroup>,
  ) {
    const notCompletedLoans = form.controls.notCompletedLoans;
    const notCompletedSectionEnabled = isBooleanLikeTrue(
      form.controls.notCompletedProjectWithinFiveYears.value,
    );
    const formIds = notCompletedSectionEnabled
      ? new Set(notCompletedLoans.controls.map((c) => c.controls.id.value))
      : new Set<string>();

    return this.financialAssetsService.getFinancialAssets(projectId).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of([]);
        }
        return throwError(() => err);
      }),
      map((res): FinancialAssetResponse[] => {
        const arr = Array.isArray(res) ? res : res ? [res] : [];
        return arr;
      }),
      switchMap((all) => {
        const sectionGExisting = all.filter(
          (a) =>
            a.projectConnectionType === NOT_PERFORMING_LOAN_CONNECTION_TYPE,
        );
        const existingIds = new Set(sectionGExisting.map((a) => a.id));

        const creates: Array<{
          rowIndex: number;
          obs: ReturnType<FinancialAssetService['createFinancialAsset']>;
        }> = [];
        const updates: Array<{
          id: string;
          obs: ReturnType<FinancialAssetService['updateFinancialAsset']>;
        }> = [];

        if (notCompletedSectionEnabled) {
          notCompletedLoans.controls.forEach((row, rowIndex) => {
            const id = row.controls.id.value;
            const payload = getFinancialAssetRequestFromNotCompletedLoanForm(
              row,
              projectId,
            );
            if (existingIds.has(id)) {
              updates.push({
                id,
                obs: this.financialAssetsService.updateFinancialAsset(
                  projectId,
                  id,
                  payload,
                ),
              });
            } else {
              creates.push({
                rowIndex,
                obs: this.financialAssetsService.createFinancialAsset(
                  projectId,
                  payload,
                ),
              });
            }
          });
        }

        const toDelete = sectionGExisting.filter((a) => !formIds.has(a.id));
        const deletes = toDelete.map((a) =>
          this.financialAssetsService.deleteFinancialAsset(projectId, a.id),
        );

        const allOps = [
          ...creates.map((c) => c.obs),
          ...updates.map((u) => u.obs),
          ...deletes,
        ];
        return allOps.length > 0
          ? forkJoin(allOps).pipe(
              tap((results) => {
                creates.forEach((c, i) => {
                  const created = results[i] as FinancialAssetResponse;
                  if (created?.id) {
                    notCompletedLoans.controls[c.rowIndex].controls.id.setValue(
                      created.id,
                      { emitEvent: false },
                    );
                  }
                });
              }),
            )
          : of([]);
      }),
    );
  }
}
